import { useState } from "react";
import { checkAccess } from "../utils/accessControl";
import { doc, getDoc, updateDoc, arrayUnion } from "firebase/firestore";
import { addMemberToStreamChannel } from "../services/streamService";
import { db } from "../firebaseConfig";
import { ChannelType } from "../config/stream";
import { handleGroupJoinComplete } from "../utils/groupJoinNotification";

export const useGroupJoining = () => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);

  const checkJoiningEligibility = (user, group) => {
    // Use the centralized access control function
    const isPremium = group.isPremium === true;
    const groupType = isPremium ? "Group Premium" : "Group Standard";
    const contentType = isPremium ? "premium-class" : "standard";

    const accessResult = checkAccess(user, contentType, groupType);

    return {
      canJoin: accessResult.hasAccess,
      useSubscription:
        accessResult.reason === "Valid group subscription" ||
        accessResult.reason === "Unlimited subscription",
      message: accessResult.reason,
    };
  };

  const joinGroup = async (
    user,
    group,
    updateContextAndSession,
    onSuccess,
    onError,
  ) => {
    console.log("[useGroupJoining] joinGroup called", { 
      userId: user?.uid, 
      groupId: group?.groupId, 
      isPremium: group?.isPremium 
    });
    
    // Prevent multiple simultaneous operations
    if (isProcessing) {
      console.log("[useGroupJoining] Already processing, skipping");
      return false;
    }

    setIsProcessing(true);
    setError(null);

    try {
      const { groupId, isPremium } = group;
      console.log("[useGroupJoining] Processing join", { groupId, isPremium });

      // Check if user has free access
      if (user.freeAccess) {
        console.log("[useGroupJoining] User has free access, using handleFreeAccessJoin");
        return await handleFreeAccessJoin(
          user,
          groupId,
          isPremium,
          updateContextAndSession,
        );
      }

      // Check eligibility for non-free users
      console.log("[useGroupJoining] Checking eligibility");
      const eligibility = checkJoiningEligibility(user, group);
      console.log("[useGroupJoining] Eligibility result:", eligibility);
      if (!eligibility.canJoin) {
        console.log("[useGroupJoining] User cannot join:", eligibility.message);
        setError(eligibility.message);
        onError?.(eligibility.message);
        return false;
      }

      console.log("[useGroupJoining] User eligible, calling handleStandardJoin");
      return await handleStandardJoin(
        user,
        groupId,
        isPremium,
        updateContextAndSession,
      );
    } catch (err) {
      const errorMessage = err.message || "Failed to join group";
      setError(errorMessage);
      onError?.(errorMessage);
      return false;
    } finally {
      setIsProcessing(false);
    }
  };

  const handleFreeAccessJoin = async (
    user,
    groupId,
    isPremium,
    updateContextAndSession,
  ) => {
    const groupRef = doc(db, "groups", groupId);
    const userRef = doc(db, "students", user.uid);

    // Get current group data
    const groupDoc = await getDoc(groupRef);
    const groupData = groupDoc.data();

    if (groupData.memberIds.includes(user.uid)) {
      return true; // Already a member
    }

    const channelType = isPremium
      ? ChannelType.PREMIUM_GROUP
      : ChannelType.STANDARD_GROUP;

    // Parallelize Firestore updates for faster execution
    await Promise.all([
      updateDoc(groupRef, { memberIds: arrayUnion(user.uid) }),
      updateDoc(userRef, { joinedGroups: arrayUnion(groupId) }),
    ]);

    // Update context and session immediately (don't wait for Stream)
    updateContextAndSession(groupId);

    // Dispatch events immediately for UI updates
    try {
      window.dispatchEvent(new CustomEvent("groupJoined", {
        detail: { userId: user.uid, groupId }
      }));
      window.dispatchEvent(new CustomEvent("userGroupsUpdated", {
        detail: { userId: user.uid }
      }));
    } catch (eventError) {
      console.warn(`[useGroupJoining] Could not dispatch event: ${eventError.message}`);
    }

    // Handle Stream operations in background (non-blocking)
    (async () => {
    try {
      await addMemberToStreamChannel({
        channelId: groupId,
        userId: user.uid,
        type: channelType,
        role: "channel_member",
      });

        // Refresh channels in background if premium (non-blocking)
        if (isPremium) {
          try {
            const { refreshUserChannels } = await import(
              "../services/streamConnectionService"
            );
            // Don't await - let it run in background
            refreshUserChannels(user.uid).catch((err) => {
              console.warn("[useGroupJoining] Background channel refresh failed:", err);
            });
          } catch (refreshError) {
            console.warn("[useGroupJoining] Error refreshing channels:", refreshError);
          }
        }
    } catch (streamError) {
        console.error("[useGroupJoining] Stream error (non-blocking):", streamError);
        // Non-blocking - user is already in Firestore as member
      }
    })();

    // Handle group join completion (non-blocking notification)
    handleGroupJoinComplete(groupId, user.uid).catch((err) => {
      console.warn("[useGroupJoining] Background join completion failed:", err);
    });

    return true;
  };

  const handleStandardJoin = async (
    user,
    groupId,
    isPremium,
    updateContextAndSession,
  ) => {
    const groupRef = doc(db, "groups", groupId);
    const userRef = doc(db, "students", user.uid);

    // Get current group data
    const groupDoc = await getDoc(groupRef);
    const groupData = groupDoc.data();

    if (groupData.memberIds.includes(user.uid)) {
      return true; // Already a member
    }

    const channelType = isPremium
      ? ChannelType.PREMIUM_GROUP
      : ChannelType.STANDARD_GROUP;

    // Parallelize Firestore updates for faster execution
    await Promise.all([
      updateDoc(groupRef, { memberIds: arrayUnion(user.uid) }),
      updateDoc(userRef, { joinedGroups: arrayUnion(groupId) }),
    ]);

    // Update context and session immediately (don't wait for Stream)
    updateContextAndSession(groupId);

    // Dispatch events immediately for UI updates
    try {
      window.dispatchEvent(new CustomEvent("groupJoined", {
        detail: { userId: user.uid, groupId }
      }));
      window.dispatchEvent(new CustomEvent("userGroupsUpdated", {
        detail: { userId: user.uid }
      }));
    } catch (eventError) {
      console.warn(`[useGroupJoining] Could not dispatch event: ${eventError.message}`);
    }

    // Handle Stream operations in background (non-blocking)
    // This allows the user to see the group immediately while Stream syncs
    (async () => {
    try {
      await addMemberToStreamChannel({
        channelId: groupId,
        userId: user.uid,
        type: channelType,
        role: "channel_member",
      });

        // Refresh channels in background (non-blocking)
        try {
          const { refreshUserChannels } = await import(
            "../services/streamConnectionService"
          );
          // Don't await - let it run in background
          refreshUserChannels(user.uid).catch((err) => {
            console.warn("[useGroupJoining] Background channel refresh failed:", err);
          });
        } catch (refreshError) {
          console.warn("[useGroupJoining] Error refreshing channels:", refreshError);
        }
      } catch (streamError) {
        console.error("[useGroupJoining] Stream error (non-blocking):", streamError);
        // Non-blocking - user is already in Firestore as member
      }
    })();

    // Handle group join completion (non-blocking notification)
    handleGroupJoinComplete(groupId, user.uid).catch((err) => {
      console.warn("[useGroupJoining] Background join completion failed:", err);
    });

    return true;
  };

  const handleGroupJoining = async (
    user,
    group,
    onSuccess,
    onFailure,
    handleJoinGroup,
  ) => {
    // Legacy method for backward compatibility
    return await joinGroup(user, group, () => {}, onSuccess, onFailure);
  };

  return {
    joinGroup,
    handleGroupJoining,
    checkJoiningEligibility,
    isProcessing,
    error,
  };
};
